/*
  Composables shared by the M* compat wrappers for the plumbing that differs between
  Vue 2 and Vue 3 regardless of which Vuetify component is wrapped.
*/
import { computed, getCurrentInstance, useSlots } from 'vue';
import type { ComputedRef } from 'vue';

/**
 * Consumer listeners to forward with `v-on` on a wrapper's Vue 2 root. Vue 2 keeps
 * listeners out of `$attrs`; in the Vue 3 build they are part of `$attrs` and this is
 * empty. `bridgedEvents` are left out because the wrapper re-emits those itself, and
 * forwarding them as well would fire the consumer's handler twice.
 */
export function useVue2Listeners(bridgedEvents: readonly string[]): ComputedRef<Record<string, unknown>> {
  // `$listeners` exists only on a Vue 2 instance, so Vue 3's instance type does not declare it.
  const proxy = getCurrentInstance()?.proxy as {
    $listeners?: Record<string, unknown>;
    $options: { name?: string; compatConfig?: { MODE?: unknown } };
  } | undefined;
  // In MODE 2, @vue/compat strips listeners from `$attrs`, so the wrapper would silently drop them.
  if (!import.meta.env.VUE_APP_V2 && proxy?.$options.compatConfig?.MODE !== 3) {
    throw new Error(`${proxy?.$options.name ?? 'Component'} uses useVue2Listeners and must declare compatConfig: { MODE: 3 }`);
  }
  return computed(() => {
    // Reading `$listeners` throws in MODE 3.
    if (!import.meta.env.VUE_APP_V2) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(proxy?.$listeners ?? {}).filter(([name]) => !bridgedEvents.includes(name)),
    );
  });
}

/**
 * Named slots to forward with `<template v-for="name in ...()" #[name]>`. The default
 * slot is always left out: a dynamic `#[name]` does not resolve `default`, so wrappers
 * forward it with a bare `<slot />`. `overriddenSlots` are those the wrapper renders itself.
 * This is a function rather than a computed because neither Vue 2 nor @vue/compat tracks
 * the set of slot names, so a computed would keep the names of the first render.
 */
export function usePassthroughSlotNames(overriddenSlots: readonly string[]): () => string[] {
  const slots = useSlots();
  return () => Object.keys(slots).filter(name => name !== 'default' && !overriddenSlots.includes(name));
}

/**
 * Named slots to forward on a wrapper's Vue 2 root, split by how Vuetify 2 looks them up.
 * Vuetify 2 finds structural slots (`no-data`, `prepend-item`, ...) on `$slots`, not
 * `$scopedSlots`, and a `v-slot` template declaring a scope variable compiles without the
 * marker that reverse-proxies it onto `$slots`. So `normalSlotNames` must be re-emitted
 * without a scope parameter, and `scopedSlotNames` (every other slot) with one.
 * These are functions rather than computeds because Vue 2 reassigns `$slots` on each parent
 * render without reactivity tracking, so a computed would go stale.
 */
export function useVue2SlotNames(overriddenSlots: readonly string[]): {
  normalSlotNames: () => string[];
  scopedSlotNames: () => string[];
} {
  const slots = useSlots();
  // `$slots` holds only the normal slots in Vue 2; `useSlots()` holds normal and scoped slots alike.
  const proxy = getCurrentInstance()?.proxy as { $slots?: Record<string, unknown> } | undefined;

  function normalSlotNames(): string[] {
    return Object.keys(proxy?.$slots ?? {}).filter(name => !overriddenSlots.includes(name));
  }

  function scopedSlotNames(): string[] {
    const normal = new Set(normalSlotNames());
    return Object.keys(slots).filter(name => !overriddenSlots.includes(name) && !normal.has(name));
  }

  return { normalSlotNames, scopedSlotNames };
}
